import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { ServiceAppointmentDataPoint } from '../../../types/dashboard.types';
import styles from './Charts.module.css';
import { useTokenColors } from '../../../lib/useTokenColors';

// AB#2193 — series/axis colours resolved from tokens (Recharts writes SVG attributes).
const CHART_TOKENS = ['primary', 'accent-orchid-fg', 'accent', 'text-secondary', 'chart-grid'] as const;

const tooltipContentStyle = {
  backgroundColor: 'var(--bg-elevated)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border-subtle)',
  borderRadius: '8px',
  padding: '8px 12px',
  fontSize: '12px',
};

interface ServiceComparisonChartProps {
  data: ServiceAppointmentDataPoint[];
  /** #1163: Actual category names from API */
  categoryNames?: string[];
  loading?: boolean;
  error?: boolean;
  incomplete?: boolean;
  title?: string;
}

const ServiceComparisonChart: React.FC<ServiceComparisonChartProps> = ({
  data,
  categoryNames,
  loading = false,
  error = false,
  incomplete = false,
  title = 'Service Appointment Comparison',
}) => {
  const tc = useTokenColors(CHART_TOKENS);
  const SERVICE_A_COLOR = tc.primary;
  const SERVICE_B_COLOR = tc['accent-orchid-fg'];
  const SERVICE_C_COLOR = tc.accent;
  // #1163: Use real category names instead of hardcoded Service A/B/C
  const nameA = categoryNames?.[0] || 'Category 1';
  const nameB = categoryNames?.[1] || 'Category 2';
  const nameC = categoryNames?.[2] || 'Category 3';

  // #1164: Export handler
  const handleExport = () => {
    if (!data || data.length === 0) return;
    const csvRows = ['Category,Value'];
    if (data[0]) {
      csvRows.push(`${nameA},${data[0].serviceA}`);
      csvRows.push(`${nameB},${data[0].serviceB}`);
      csvRows.push(`${nameC},${data[0].serviceC}`);
    }
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'service-comparison.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // #1183: Calculate Y-axis domain from actual data instead of hardcoded 60
  const maxValue = data && data.length > 0
    ? Math.max(...data.map(d => Math.max(d.serviceA || 0, d.serviceB || 0, d.serviceC || 0)))
    : 0;
  const yDomain: [number, number] = [0, maxValue > 0 ? Math.ceil(maxValue * 1.2) : 10];

  if (loading) {
    return (
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h3 className={styles.chartTitle}>{title}</h3>
        </div>
        <div className={`${styles.chartContainer} ${styles.chartLoading}`}>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h3 className={styles.chartTitle}>{title}</h3>
        </div>
        <div className={`${styles.chartContainer} ${styles.chartError}`}>
          Error generating graph. Please refresh the page.
        </div>
      </div>
    );
  }

  if (incomplete || !data || data.length === 0) {
    return (
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h3 className={styles.chartTitle}>{title}</h3>
        </div>
        <div className={`${styles.chartContainer} ${styles.chartIncomplete}`}>
          {!data || data.length === 0
            ? 'No data available'
            : 'Incomplete data. Graph may not be accurate'}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <h3 className={styles.chartTitle}>{title}</h3>
        {/* #1164: Wire export button */}
        <button type="button" className={styles.exportBtn} aria-label="Export" onClick={handleExport}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Export
        </button>
      </div>
      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={tc['chart-grid']} vertical={false} />
            <XAxis
              dataKey="monthLabel"
              tick={{ fill: tc['text-secondary'], fontSize: 12 }}
              axisLine={{ stroke: tc['chart-grid'] }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: tc['text-secondary'], fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              domain={yDomain}
              tickCount={5}
            />
            <Tooltip
              contentStyle={tooltipContentStyle}
              itemStyle={{ color: 'var(--text-primary)' }}
              formatter={(value: number, name: string) => [
                value,
                name === 'serviceA' ? nameA : name === 'serviceB' ? nameB : nameC,
              ]}
              labelFormatter={(label) => label}
            />
            <Legend
              verticalAlign="bottom"
              height={36}
              iconType="circle"
              iconSize={8}
              formatter={(value) => <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{value}</span>}
            />
            <Line
              type="monotone"
              dataKey="serviceA"
              name={nameA}
              stroke={SERVICE_A_COLOR}
              strokeWidth={2}
              dot={{ fill: SERVICE_A_COLOR, r: 3 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="serviceB"
              name={nameB}
              stroke={SERVICE_B_COLOR}
              strokeWidth={2}
              dot={{ fill: SERVICE_B_COLOR, r: 3 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="serviceC"
              name={nameC}
              stroke={SERVICE_C_COLOR}
              strokeWidth={2}
              dot={{ fill: SERVICE_C_COLOR, r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ServiceComparisonChart;
